import type { SerializedObjectId, StrictOmit } from '@enmon/commons';
import {
  type Collection,
  type CountableApiResponse,
  type CreateCustomerDto,
  type CustomerIntegrationConfig,
  type SerializedAuditLog,
  type SerializedCustomer,
  type SerializedCustomerWithLicensesStats,
  type SerializedFilter,
  type SerializedUser,
  type SplitBy,
  type UpdateCustomerDto,
  ViewCustomers,
} from '@enmon/commons/lib/enmon';
import type { UploadFile } from 'antd';
import { AxiosError } from 'axios';
import { formErrorSchema } from 'components/form/error';
import type { Pagination } from 'types/backendGateway/pagination';
import { z } from 'zod';
import { BaseApiClient } from '../baseApiClient';
import { ValidationFailedError } from '../validation-failed.error';

export interface AuditLogsFilter extends Pagination {
  collections?: Collection[];
  collectionDocumentId?: SerializedObjectId;
}

export type PopulatedAuditLog = StrictOmit<SerializedAuditLog, 'userId'> & {
  userId: {
    _id: SerializedUser['_id'];
    email: SerializedUser['email'];
    name: SerializedUser['name'];
  };
};

// View types
export type CustomerDefaultView = SerializedCustomer;
export type CustomerMetadataView = Pick<SerializedCustomer, '_id' | 'name'>;

// Arg types
export type GetCustomerArg = SerializedCustomer['_id'];
export type GetCustomersArg = { filter: SerializedFilter; view?: ViewCustomers };
export type GetCustomerIntegrationConfigArg = SerializedCustomer['_id'];
export type GetLicensesArg = Pick<SerializedFilter, 'customer' | 'depth' | 'search'> & {
  split?: SplitBy;
};
export type GetResellerStatsArg = SerializedCustomer['_id'];
export type GetAuditLogsArg = AuditLogsFilter;
export type CreateCustomerArg = {
  payload: CreateCustomerDto;
  logo?: UploadFile<File>;
};
export type UpdateCustomerArg = {
  customerId: SerializedCustomer['_id'];
  payload: UpdateCustomerDto;
  logo?: UploadFile<File>;
};

// Response types
export type GetCustomerResponse = SerializedCustomer;
export type CustomerViewResponse<TView> = TView extends ViewCustomers.Default
  ? CustomerDefaultView[]
  : CustomerMetadataView[];

export type GetCustomersResponse<TView extends ViewCustomers = ViewCustomers.Default> = CountableApiResponse<
  CustomerViewResponse<TView>
> | null;

export type GetLicensesStatisticsResponse = CountableApiResponse<SerializedCustomerWithLicensesStats[]> | null;

export type GetCustomerIntegrationConfigResponse = CustomerIntegrationConfig | null;

export type GetAuditLogsResponse = CountableApiResponse<PopulatedAuditLog[]> | null;

export interface ParseInvoiceArgs {
  customer: string | number;
  file: File | Blob;
}

export interface ParseInvoiceResponse {
  status: 'error' | 'ok';
  pages: unknown[];
  parsed: unknown[];
  errors: unknown[];
}

export class BackendGatewayCustomersApiClient extends BaseApiClient {
  constructor(jwt: string) {
    super('enmon-tools/customers', undefined, jwt);
  }

  public async find<TView extends ViewCustomers = ViewCustomers.Default>(
    { filter, view }: GetCustomersArg = { filter: {}, view: ViewCustomers.Default },
  ): Promise<GetCustomersResponse<TView>> {
    const params = { ...filter, view };

    const response = await this.axios.get<GetCustomersResponse<TView>>('', { params });
    return response.data;
  }

  public async findById(customerId: GetCustomerArg): Promise<GetCustomerResponse> {
    // GUARD CLAUSE: Throw early if id is missing or empty
    if (!customerId) {
      throw new Error('findById requires a valid customerId');
    }

    const response = await this.axios.get<GetCustomerResponse>(customerId);

    return response.data;
  }

  public async getIntegrationConfig(
    customerId: GetCustomerIntegrationConfigArg,
  ): Promise<GetCustomerIntegrationConfigResponse> {
    const response = await this.axios.get<GetCustomerIntegrationConfigResponse>(`/${customerId}/integration-config`);
    return response.data;
  }

  public async getLicensesStatistics(filter: GetLicensesArg = {}): Promise<GetLicensesStatisticsResponse> {
    const { customer, search, split, depth } = filter;

    const params = new URLSearchParams();

    if (customer) params.append('customers', customer);

    if (search) params.append('search', search);

    if (split) params.append('split', split);

    if (depth) params.append('depth', depth.toString());

    const response = await this.axios.get<GetLicensesStatisticsResponse>('/stats', {
      params: params.toString(),
    });

    return response.data;
  }

  public async create({ payload, logo }: CreateCustomerArg): Promise<SerializedCustomer> {
    try {
      const formData = new FormData();
      formData.append('data', JSON.stringify(payload));

      if (logo?.originFileObj) {
        formData.append('files.logo', logo.originFileObj);
      }

      const response = await this.axios.post<SerializedCustomer>('', formData);
      return response.data;
    } catch (e) {
      if (e instanceof AxiosError && e.response?.status === 412) {
        const response = z
          .object({
            error: z.object({
              details: z.array(formErrorSchema),
            }),
          })
          .parse(e.response.data);
        throw new ValidationFailedError(response.error.details);
      }
      throw e;
    }
  }

  public async update({ customerId, payload, logo }: UpdateCustomerArg): Promise<SerializedCustomer> {
    try {
      const formData = new FormData();
      formData.append('data', JSON.stringify(payload));

      if (logo?.originFileObj) {
        formData.append('files.logo', logo.originFileObj);
      }
      const response = await this.axios.patch<SerializedCustomer>(customerId, formData);
      return response.data;
    } catch (e) {
      if (e instanceof AxiosError && e.response?.status === 412) {
        const response = z
          .object({
            error: z.object({
              details: z.array(formErrorSchema),
            }),
          })
          .parse(e.response.data);
        throw new ValidationFailedError(response.error.details);
      }
      throw e;
    }
  }

  public async delete(customerId: SerializedCustomer['_id']): Promise<void> {
    await this.axios.delete<SerializedCustomer>(customerId);
  }

  public async getAuditLogs(
    customerId: SerializedCustomer['_id'],
    filter: GetAuditLogsArg,
  ): Promise<GetAuditLogsResponse> {
    const response = await this.axios.get<GetAuditLogsResponse>(`/${customerId}/audit-logs`, {
      params: filter,
    });

    return response.data;
  }
}
